import React, { useEffect } from 'react';
import propTypes from 'prop-types';
import axios from 'axios';
import { useSelector } from 'react-redux';
import ComponentContainer from 'tailwind-components/ComponentContainer/ComponentContainer';
import ContentWithSidebar from 'tailwind-components/ContentWithSidebar/ContentWithSidebar';
import AnnouncementsCarousel from 'tailwind-components/AnnouncementsCarousel/AnnouncementsCarousel';
import CalendarSection from 'tailwind-components/Dashboard/CalendarSection/CalendarSection';
import ProfileApptBanner from 'tailwind-components/Dashboard/ProfileApptBanner';
import {
  areAnnouncementsHidden,
  areCommissionsTabVisible
} from 'helpers/viewing-rules';
import CommissionsByMonthCard from './CommissionsByMonthCard';
import ConsultantCard from './ConsultantCard';
import NotesCard from './NotesCard';
import QuickLinksCard from './QuickLinksCard';

const ANNOUNCEMENTS_TIMER_MS = 6000;

// the side column keeps a fixed width so the calendar gets every spare pixel
const SIDEBAR_GRID = 'lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]';

/**
 * sunshine home screen: full-width welcome banner with the appointment counters, then
 * commissions and announcements over a calendar-driven main column, with notes,
 * shortcuts and the assigned consultant on the side
 * @param user - zoho data of the logged agent
 * @param announcements - carousel slides coming from the dashboard endpoint
 * @param notes - the agent's portal notes, keyed by id
 * @param importantDates - company-wide dates rendered in the calendar
 * @param calendarEvents - the agent's own calendar events
 * @param getDashboardData - action that loads every block of this screen
 */
export default function SunshineDashboard({
  user,
  announcements,
  notes,
  importantDates,
  calendarEvents,
  getDashboardData
}) {
  const loadingDashboard = useSelector(
    state => state.dashboard.loadingDashboard
  );

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();
    getDashboardData(cancelTokenSource);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, [user?.id, getDashboardData]);

  return (
    <ComponentContainer className='h-auto gap-3 overflow-y-auto lg:h-full lg:overflow-y-hidden'>
      <ProfileApptBanner user={user} />
      <ContentWithSidebar
        desktopSidebarGridClassName={SIDEBAR_GRID}
        mainContent={
          <div className='flex min-w-0 flex-col gap-3'>
            {areCommissionsTabVisible(user) && (
              <CommissionsByMonthCard user={user} />
            )}
            {!areAnnouncementsHidden(user?.agencyData) && (
              <AnnouncementsCarousel
                data={announcements ?? []}
                timer={ANNOUNCEMENTS_TIMER_MS}
              />
            )}
            <CalendarSection
              importantDates={importantDates}
              calendarEvents={calendarEvents}
            />
          </div>
        }
        sidebarContent={
          <div className='flex min-w-0 flex-col gap-3 lg:h-full'>
            <ConsultantCard />
            <QuickLinksCard user={user} />
            <NotesCard
              notes={notes ? Object.values(notes) : []}
              loading={loadingDashboard}
            />
          </div>
        }
      />
    </ComponentContainer>
  );
}

SunshineDashboard.propTypes = {
  user: propTypes.object.isRequired,
  announcements: propTypes.array,
  notes: propTypes.object,
  importantDates: propTypes.array,
  calendarEvents: propTypes.array,
  getDashboardData: propTypes.func
};
